import React, {useEffect, useRef, useState} from "react";
import {Button, Input, Modal, Select, Space, Table, Tag, Tooltip, message} from "antd";
import {SectionToolbar} from "../../../components/SectionToolbar/SectionToolbar";
import {PlusOutlined} from "@ant-design/icons";
import {observer} from "mobx-react-lite";
import {usersErrorText, usersModel} from "../../../../../../entities/users";
import {rolesModel} from "../../../../../../entities/roles";
import {useCanEdit} from "../../../../sectionAccess";
import {authModel} from "../../../../../../entities/auth";
import {apiInstance} from "../../../../../../shared/api/api";
import {config} from "../../../../../../shared/config/config";

const roleTitle = (role) => role.title || role.name

// the user's roles as tags (several roles add up their rights)
const roleTags = (roles, ids) => {
  const list = (ids || []).map((id) => roles.find((r) => r.id === id)).filter(Boolean)
  if (!list.length) return <span className='cell-empty'>—</span>
  return <div className='tag-list'>{list.map((role) => <Tag key={role.id} color={role.name === 'admin' ? 'gold' : undefined}>{roleTitle(role)}</Tag>)}</div>
}

const fullName = ({lastName, name}) => [lastName, name].filter(Boolean).join(' ') || 'Без имени'

const formatDate = (value, withTime) => {
  if (!value) return null
  const date = new Date(value)
  const day = date.toLocaleDateString('ru-RU', {day: '2-digit', month: '2-digit', year: 'numeric'})
  return withTime ? `${day} ${date.toLocaleTimeString('ru-RU', {hour: '2-digit', minute: '2-digit'})}` : day
}

// 79061234506 -> +7 (906) 123-45-06
const formatPhone = (phone) => {
  const d = String(phone || '').replace(/\D/g, '')
  return d.length === 11 ? `+7 (${d.slice(1, 4)}) ${d.slice(4, 7)}-${d.slice(7, 9)}-${d.slice(9)}` : phone
}

// a new password for someone who forgot theirs; their open sessions end
const PasswordModal = ({user, onClose}) => {
  const [password, setPassword] = useState('')
  const [repeat, setRepeat] = useState('')
  const [busy, setBusy] = useState(false)
  const error = password && password.length < 8
    ? 'Не короче 8 символов'
    : repeat && repeat !== password ? 'Пароли не совпадают' : null

  const save = async () => {
    setBusy(true)
    try {
      await usersModel.setPassword(user.id, password)
      message.success(`Пароль для ${fullName(user)} изменён`)
      onClose()
    } catch (err) {
      message.error(usersErrorText(err))
    }
    setBusy(false)
  }

  return (
    <Modal
      visible
      title={`Сменить пароль: ${fullName(user)}`}
      okText='Сменить пароль'
      cancelText='Отмена'
      okButtonProps={{disabled: !password || password !== repeat || password.length < 8, loading: busy}}
      onOk={save}
      onCancel={onClose}
    >
      <Space direction='vertical' style={{width: '100%'}}>
        <Input.Password placeholder='Новый пароль, не короче 8 символов' value={password} onChange={(e) => setPassword(e.target.value)} autoComplete='new-password'/>
        <Input.Password placeholder='Повторите пароль' value={repeat} onChange={(e) => setRepeat(e.target.value)} autoComplete='new-password'/>
        {error && <div style={{color: '#cf1322'}}>{error}</div>}
        <div style={{color: '#8c8c8c'}}>Пользователя выкинет со всех устройств, войти можно будет только с новым паролем.</div>
      </Space>
    </Modal>
  )
}

// what the user's roles open: the portal, the panel (admins are not looked at as)
const placesOf = (roles, ids) => {
  const list = (ids || []).map((id) => roles.find((r) => r.id === id)).filter(Boolean)
  return {
    admin: list.some((r) => r.name === 'admin'),
    portal: list.some((r) => r.permissions && r.permissions.portal),
    panel: list.some((r) => r.permissions && Object.keys(r.permissions.sections || {}).length),
  }
}

// «Смотреть как»: a new tab of the portal or the panel as this user, read-only; the code is good for a minute
const ImpersonateModal = ({user, roles, onClose}) => {
  const places = placesOf(roles, user.roleIds || [user.roleId])
  const [busy, setBusy] = useState(null)

  const open = async (app) => {
    // opened now, inside the click, or the browser blocks it
    const tab = window.open('', '_blank')
    if (tab) tab.opener = null
    setBusy(app)
    try {
      const {data} = await apiInstance.post(`/users/${user.id}/impersonate`, {app})
      const base = app === 'portal' ? config.PORTAL_URL : window.location.origin
      if (tab) tab.location.href = `${base}/impersonate#${data.code}`
      else window.location.href = `${base}/impersonate#${data.code}`
      onClose()
    } catch (err) {
      if (tab) tab.close()
      message.error(usersErrorText(err))
    }
    setBusy(null)
  }

  return (
    <Modal visible title={`Смотреть как ${fullName(user)}`} footer={null} onCancel={onClose}>
      <Space direction='vertical' style={{width: '100%'}}>
        <div style={{color: '#595959'}}>
          Откроется новая вкладка: всё как видит этот пользователь, но только для просмотра — бронировать, отправлять и
          менять ничего нельзя. Ваш вход не меняется; сессия просмотра закрывается кнопкой «Закрыть» или через 2 часа.
        </div>
        <Space wrap>
          <Button type='primary' disabled={!places.portal} loading={busy === 'portal'} onClick={() => open('portal')}>Портал агента</Button>
          <Button disabled={!places.panel} loading={busy === 'admin'} onClick={() => open('admin')}>Админка</Button>
        </Space>
        {!places.portal && !places.panel && <div style={{color: '#8c8c8c'}}>У ролей пользователя нет ни портала, ни разделов админки.</div>}
      </Space>
    </Modal>
  )
}

// the user cannot log in and is logged out at once; the reason is shown to them when they try
const BlockModal = ({user, onClose}) => {
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)

  const block = async () => {
    setBusy(true)
    try {
      await usersModel.block(user.id, reason)
      message.success(`${fullName(user)} заблокирован`)
      onClose()
    } catch (err) {
      message.error(usersErrorText(err))
    }
    setBusy(false)
  }

  return (
    <Modal
      visible
      title={`Заблокировать: ${fullName(user)}`}
      okText='Заблокировать'
      okButtonProps={{danger: true, loading: busy}}
      cancelText='Отмена'
      onOk={block}
      onCancel={onClose}
    >
      <Space direction='vertical' style={{width: '100%'}}>
        <Input.TextArea rows={3} maxLength={500} placeholder='Причина (увидит пользователь при попытке войти), необязательно' value={reason} onChange={(e) => setReason(e.target.value)}/>
        <div style={{color: '#8c8c8c'}}>Войти не получится, открытый портал выкинет при следующем действии. Разблокировать можно в любой момент.</div>
      </Space>
    </Modal>
  )
}

// who the user was on ECI old (by phone or e-mail) and the role that suggests; one button gives it instead of «Агент»
const OldStaffHint = ({user, canEdit}) => {
  const [busy, setBusy] = useState(false)
  const {group, roleId, roleTitle} = user.oldStaff
  const give = () => Modal.confirm({
    title: `Дать роль «${roleTitle}»?`,
    content: `${fullName(user)}: на старом сайте — «${group}». Роль «Агент» заменится на «${roleTitle}».`,
    okText: 'Дать роль',
    cancelText: 'Отмена',
    onOk: async () => {
      setBusy(true)
      try {
        await usersModel.setRoles(user.id, [roleId])
        message.success(`${fullName(user)}: роль «${roleTitle}»`)
      } catch (err) {
        message.error(usersErrorText(err))
      }
      setBusy(false)
    },
  })
  return (
    <div className='old-staff-hint'>
      <span>На старом: {group}</span>
      {roleId && canEdit && <Button size='small' type='link' loading={busy} onClick={give}>Дать роль «{roleTitle}»</Button>}
      {!roleId && <span className='cell-hint'> — роль подберите сами</span>}
    </div>
  )
}

export const UsersTable = observer(({form, setOpenedDrawer, handleResponse, setEditableUserId}) => {
  const canEdit = useCanEdit()
  // read here, not only inside antd's cell renders, so the table redraws when roles arrive
  const roles = rolesModel.roles
  const {role: roleFilter, status, sort, blocked, total} = usersModel
  const [search, setSearch] = useState(usersModel.search)
  const [passwordUser, setPasswordUser] = useState(null)
  const [blockUser, setBlockUser] = useState(null)
  const [viewAs, setViewAs] = useState(null)
  const timer = useRef(null)

  useEffect(() => () => clearTimeout(timer.current), [])

  const onSearch = (value) => {
    setSearch(value)
    clearTimeout(timer.current)
    timer.current = setTimeout(() => usersModel.setFilters({search: value.trim()}), 400)
  }

  const unblock = (user) => Modal.confirm({
    title: `Разблокировать: ${fullName(user)}?`,
    content: 'Пользователь снова сможет входить со своим паролем.',
    okText: 'Разблокировать',
    cancelText: 'Отмена',
    onOk: async () => {
      try {
        await usersModel.unblock(user.id)
        message.success(`${fullName(user)} разблокирован`)
      } catch (err) {
        message.error(usersErrorText(err))
      }
    },
  })

  const remove = (user) => Modal.confirm({
    title: `Удалить пользователя ${fullName(user)}?`,
    content: 'Учётная запись удалится насовсем, отменить это нельзя. Если нужно только закрыть доступ — лучше заблокировать.',
    okText: 'Удалить насовсем',
    okButtonProps: {danger: true},
    cancelText: 'Отмена',
    onOk: async () => {
      try {
        const response = await usersModel.delete(user.id)
        handleResponse(response)
      } catch (err) {
        message.error(usersErrorText(err))
      }
    },
  })

  const usersTableColumns = [
    {
      title: 'Пользователь',
      key: 'name',
      render: (user) => (
        <>
          <div style={{fontWeight: 600}}>{fullName(user)}</div>
          <div style={{color: '#8c8c8c', fontSize: 12}}>логин: {user.login}</div>
        </>
      ),
    },
    {
      title: 'Контакты',
      key: 'contacts',
      render: ({phone, email}) => (
        <>
          <div style={{whiteSpace: 'nowrap'}}>{formatPhone(phone)}</div>
          <div style={{color: '#595959', fontSize: 12, wordBreak: 'break-all'}}>{email}</div>
        </>
      ),
    },
    {
      title: 'Роли',
      key: 'roleIds',
      render: (user) => (
        <>
          {roleTags(roles, user.roleIds || (user.roleId ? [user.roleId] : []))}
          {user.oldStaff && <OldStaffHint user={user} canEdit={canEdit}/>}
        </>
      ),
    },
    {title: 'Регистрация', key: 'createdAt', render: ({createdAt}) => formatDate(createdAt) || '—'},
    {
      title: 'Последний вход',
      key: 'lastLoginAt',
      render: ({lastLoginAt}) => formatDate(lastLoginAt, true) || <span style={{color: '#bfbfbf'}}>нет данных</span>,
    },
    {
      title: 'Статус',
      key: 'status',
      render: ({blockedAt, blockedReason}) => (blockedAt
        ? (
          <Tooltip title={`С ${formatDate(blockedAt, true)}${blockedReason ? `. Причина: ${blockedReason}` : ''}`}>
            <Tag color='red'>Заблокирован</Tag>
          </Tooltip>
        )
        : <Tag color='green'>Активен</Tag>),
    },
    canEdit && {
      title: 'Действия',
      key: 'action',
      width: 250,
      render: (user) => (
        <Space size={[6, 6]} wrap>
          <Button
            size='small'
            onClick={() => {
              form.setFieldsValue(user)
              setOpenedDrawer(true)
              setEditableUserId(user.id)
            }}
          >
            Изменить
          </Button>
          <Button size='small' onClick={() => setPasswordUser(user)}>Сменить пароль</Button>
          {authModel.isAdmin && !user.blockedAt && !placesOf(roles, user.roleIds).admin && (
            <Button size='small' onClick={() => setViewAs(user)}>Смотреть как</Button>
          )}
          {user.blockedAt
            ? <Button size='small' onClick={() => unblock(user)}>Разблокировать</Button>
            : <Button size='small' danger onClick={() => setBlockUser(user)}>Заблокировать</Button>}
          <Button size='small' type='link' danger onClick={() => remove(user)}>Удалить</Button>
        </Space>
      ),
    },
  ].filter(Boolean)

  return (
    <>
      <Table
        title={() => (
          <SectionToolbar
            search={search}
            onSearch={onSearch}
            placeholder='Имя, фамилия, телефон или почта'
            count={`Всего ${total}`}
            filters={(
              <>
                <Select
                  allowClear
                  style={{width: 170}}
                  placeholder='Все роли'
                  value={roleFilter}
                  onChange={(role) => usersModel.setFilters({role})}
                >
                  {roles.map((role) => <Select.Option key={role.id} value={role.id}>{roleTitle(role)}</Select.Option>)}
                </Select>
                <Select
                  allowClear
                  style={{width: 185}}
                  placeholder='Любой статус'
                  value={status}
                  onChange={(status) => usersModel.setFilters({status})}
                >
                  <Select.Option value='active'>Активные</Select.Option>
                  <Select.Option value='blocked'>Заблокированные ({blocked})</Select.Option>
                  <Select.Option value='oldStaff'>Сотрудники старого сайта без роли</Select.Option>
                </Select>
                <Select style={{width: 185}} value={sort} onChange={(value) => usersModel.setFilters({sort: value})}>
                  <Select.Option value='createdAt'>Сначала новые</Select.Option>
                  <Select.Option value='lastLoginAt'>По последнему входу</Select.Option>
                  <Select.Option value='name'>По фамилии</Select.Option>
                </Select>
              </>
            )}
          >
            {canEdit && (
              <Button type="primary" icon={<PlusOutlined/>} onClick={() => setOpenedDrawer(true)}>
                Создать пользователя
              </Button>
            )}
          </SectionToolbar>
        )}
        columns={usersTableColumns}
        dataSource={usersModel.users}
        rowKey='id'
        locale={{emptyText: 'Никого не нашлось'}}
        loading={usersModel.isLoading}
        rowClassName={(user) => (user.blockedAt ? 'user-row-blocked' : '')}
        pagination={{
          current: usersModel.page,
          pageSize: usersModel.limit,
          total: usersModel.total,
          showSizeChanger: true,
          pageSizeOptions: ['20', '50', '100'],
          locale: {items_per_page: '/ стр.'},
          showTotal: (total, [from, to]) => `${from}–${to} из ${total}`,
          onChange: (page, pageSize) => usersModel.setPage(page, pageSize),
        }}
      />
      {passwordUser && <PasswordModal user={passwordUser} onClose={() => setPasswordUser(null)}/>}
      {blockUser && <BlockModal user={blockUser} onClose={() => setBlockUser(null)}/>}
      {viewAs && <ImpersonateModal user={viewAs} roles={roles} onClose={() => setViewAs(null)}/>}
    </>
  )
})
